import { QueryClient } from '@tanstack/react-query';
import { isRetryableError } from './errors.js';

/**
 * Query defaults tuned for a shop counter, not a dashboard on a fibre line.
 *
 * The device is a mid-range Android phone on patchy 4G, and the owner switches between the
 * order they are booking and the customer's last measurements constantly. So: data stays fresh
 * for half a minute (no refetch storm while flicking between screens), the cache survives a day
 * (so yesterday's order list still renders instantly, and is still there when the connection
 * drops), a failed request retries only when retrying could plausibly help, and everything
 * refetches the moment the connection comes back.
 *
 * `networkMode: 'online'` for queries is deliberate: while offline a query PAUSES and keeps
 * serving whatever the cache holds. The offline banner then tells the truth — "showing saved
 * data" — instead of a spinner that never resolves.
 *
 * Mutations are handled by the write queue (`lib/offline`), not by retries here.
 */

/** How long a fetched result is considered current. */
export const DEFAULT_STALE_TIME_MS = 30_000;

/** How long an unused result stays in the cache — long enough to survive an offline stretch. */
export const DEFAULT_GC_TIME_MS = 24 * 60 * 60 * 1000;

const MAX_QUERY_RETRIES = 3;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        networkMode: 'online',
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        retry: (failureCount, error) => failureCount < MAX_QUERY_RETRIES && isRetryableError(error),
        // 300ms, 900ms, 2.7s — quick enough to feel like a hiccup, slow enough to let a
        // tower handover finish.
        retryDelay: (attempt) => Math.min(300 * 3 ** attempt, 5_000),
      },
      mutations: {
        // A write is either sent now or queued by `useQueueableMutation`. Never blindly retried:
        // a duplicated payment entry is worse than a failed one.
        networkMode: 'online',
        retry: false,
      },
    },
  });
}
